import { Shell, Ctas, Credits, Note } from "../shared";
import { GlassMasthead } from "@/components/GlassMasthead";
import { useReader, useRtl } from "../ReaderContext";

export const PortraitPage = ({ page }) => {
  const { edition } = useReader();
  return (
    <article className="mag-page" data-testid={`mag-page-${page.n}`} lang={edition?.lang} dir={edition?.dir || "ltr"}>
      <img src={page.image} alt={page.title} className="mag-img absolute inset-0" />
      <div className="absolute inset-x-0 bottom-0" style={{ height: 180, background: "linear-gradient(180deg, rgba(25,25,25,0) 0%, rgba(25,25,25,0.7) 100%)" }} />
      <div className="absolute top-7 left-11 mag-kicker" style={{ color: "#fff", textShadow: "0 1px 8px rgba(0,0,0,0.6)" }}>{page.kicker}</div>
      <div className="absolute left-11 right-11" style={{ bottom: 48 }}>
        <h2 className="mag-head uppercase" style={{ fontSize: 34, color: "#fff" }}>{page.title}</h2>
        <Credits page={page} className="mt-3" />
      </div>
      <div className="absolute bottom-5 right-11 mag-kicker" style={{ color: "rgba(255,255,255,0.7)", fontSize: 8 }}>{String(page.n).padStart(2, "0")}</div>
    </article>
  );
};

export const SectionOpenerPage = ({ page }) => {
  const rtl = useRtl();
  return (
  <Shell page={page} dark className="grain">
    <div className={`absolute inset-y-0 ${rtl ? "left-0" : "right-0"}`} style={{ width: 330 }}>
      <img src={page.image} alt={page.title} className="mag-img" style={{ filter: "saturate(0.9)" }} />
      <div className="absolute inset-0" style={{ background: `linear-gradient(${rtl ? "270deg" : "90deg"}, #191919 0%, rgba(25,25,25,0.35) 35%, rgba(25,25,25,0) 100%)` }} />
    </div>
    <div className={`absolute ${rtl ? "right-11" : "left-11"}`} style={{ top: 150, width: 330 }}>
      <div className="mag-kicker" style={{ color: "var(--platinum)" }}>{page.kicker}</div>
      <h2 className="mt-5" style={{ fontSize: 60, lineHeight: 0.92 }}>
        <GlassMasthead text={page.title} dark style={{ fontSize: "inherit", lineHeight: "inherit", textTransform: "uppercase", letterSpacing: "-0.02em" }} />
      </h2>
      <div className="silver-rule mt-8" style={{ opacity: 0.6 }} />
      <p className="mag-kicker mt-6" style={{ color: "var(--platinum)", letterSpacing: "0.34em" }}>{page.deck}</p>
      <div className="mag-body mt-6" style={{ color: "var(--platinum-light)", maxWidth: 290 }}>
        {page.body?.map((p, i) => <p key={i}>{p}</p>)}
      </div>
    </div>
  </Shell>
  );
};

export const EssayPage = ({ page }) => {
  const imgs = page.images || [];
  const dark = page.dark;
  return (
    <Shell page={page} dark={dark}>
      <div className="absolute left-11 right-11" style={{ top: 64, bottom: 56 }}>
        <div className="mag-kicker" style={{ color: dark ? "var(--platinum)" : "var(--bordeaux)" }}>{page.kicker}</div>
        <h2 className="mag-head uppercase mt-2" style={{ fontSize: 36 }}>{page.title}</h2>
        {page.deck && <p className="mag-kicker mt-2" style={{ fontSize: 8, opacity: 0.7 }}>{page.deck}</p>}
        <div className="mt-4 grid gap-3" style={{ gridTemplateColumns: imgs.length === 3 ? "1.4fr 1fr" : "1fr", gridAutoRows: imgs.length === 1 ? 330 : imgs.length === 2 ? 210 : 190 }}>
          {imgs.map((im, i) => (
            <figure key={i} className="relative overflow-hidden" style={imgs.length === 3 && i === 0 ? { gridRow: "span 2" } : {}}>
              <img src={im.src} alt={im.caption} className="mag-img" style={{ objectPosition: im.src.includes("cover") ? "50% 34%" : "50% 30%" }} />
            </figure>
          ))}
        </div>
        <div className="mt-3 grid gap-x-4" style={{ gridTemplateColumns: `repeat(${Math.max(imgs.length, 1)}, 1fr)` }}>
          {imgs.map((im, i) => <figcaption key={i} className="mag-caption">{im.caption}</figcaption>)}
        </div>
        {page.body?.length > 0 && (
          <div className="mag-body mt-4" style={{ maxWidth: 480 }}>
            {page.body.map((p, i) => <p key={i}>{p}</p>)}
          </div>
        )}
        <Ctas page={page} className="mt-4" />
        <div className="absolute bottom-0 left-0 right-0">
          <Credits page={page} />
          <Note page={page} className="mt-1" />
        </div>
      </div>
    </Shell>
  );
};

export const DiaryPage = ({ page }) => (
  <Shell page={page}>
    <div className="absolute left-11 right-11" style={{ top: 64 }}>
      <div style={{ height: 230, overflow: "hidden" }}>
        <img src={page.image} alt={page.title} className="mag-img" />
      </div>
      <div className="mag-kicker mt-5" style={{ color: "var(--bordeaux)" }}>{page.kicker}</div>
      <h2 className="mag-head mt-1" style={{ fontSize: 36 }}>{page.title}</h2>
      <p className="mag-deck mt-1" style={{ opacity: 0.85 }}>{page.deck}</p>
      <div className="grid grid-cols-2 gap-x-6 gap-y-5 mt-5">
        {page.items.map((it, i) => (
          <div key={i} style={{ borderTop: "1px solid var(--platinum)", paddingTop: 8 }}>
            <div className="font-label" style={{ fontSize: 10, letterSpacing: "0.2em", color: "var(--bordeaux)" }}>{it.title}</div>
            <p className="mag-body mt-1" style={{ fontSize: 11.5 }}>{it.text}</p>
          </div>
        ))}
      </div>
    </div>
  </Shell>
);

export const CardsPage = ({ page }) => (
  <Shell page={page}>
    <div className="absolute left-11 right-11" style={{ top: 64 }}>
      <div className="mag-kicker" style={{ color: "var(--bordeaux)" }}>{page.kicker}</div>
      <h2 className="mag-head mt-1" style={{ fontSize: 34 }}>{page.title}</h2>
      <div className="grid grid-cols-2 gap-x-5 gap-y-5 mt-4">
        {page.items.map((it, i) => (
          <div key={i} data-testid={`page-${page.n}-card-${i}`}>
            <div style={{ height: 200, overflow: "hidden", background: "var(--platinum-light)" }}>
              {it.image && <img src={it.image} alt={it.title} className="mag-img" />}
            </div>
            <div className="mt-2 font-head font-semibold" style={{ fontSize: 19, lineHeight: 1 }}>{it.title}</div>
            <div className="mag-kicker mt-1" style={{ fontSize: 7.5, color: "var(--bordeaux)" }}>{it.meta}</div>
            <p className="mag-body mt-1" style={{ fontSize: 11 }}>{it.text}</p>
          </div>
        ))}
      </div>
      <Note page={page} className="mt-4" />
    </div>
  </Shell>
);
